// Direct Print for bills and patient documents — fetches what to print with
// the user's session and hands it to lib/print.ts. Nothing here creates or
// changes a record: printing is a read (a document print is audit-logged as
// a read on the server).
import { getActiveBranchId, getAuthToken } from '@/lib/api/session';
import { printBlob, printHtml, printJob, type PrintResult } from '@/lib/print';

export type BillPaper = 'a4' | 'receipt';

const PAPER_KEY = 'hms_bill_print_paper';

/** The paper this browser last printed bills on (a per-counter convenience). */
export function rememberedPaper(): BillPaper {
  try { return localStorage.getItem(PAPER_KEY) === 'receipt' ? 'receipt' : 'a4'; } catch { return 'a4'; }
}
function rememberPaper(paper: BillPaper) {
  try { localStorage.setItem(PAPER_KEY, paper); } catch { /* storage unavailable — default next time */ }
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const branchId = getActiveBranchId();
  if (branchId !== null) headers['X-Branch-Id'] = String(branchId);
  return headers;
}

async function failure(res: Response): Promise<Error & { code?: string }> {
  const body = (await res.json().catch(() => ({}))) as { error?: string; details?: { code?: string } };
  const err = new Error(body.error || `Request failed (${res.status})`) as Error & { code?: string };
  err.code = body.details?.code;
  return err;
}

/** Prints a bill's invoice — A4 (the stored invoice) or an 80 mm receipt. */
export function printBill(billId: number, paper: BillPaper = 'a4'): Promise<PrintResult | null> {
  rememberPaper(paper);
  return printJob(`bill:${billId}`, async () => {
    const res = await fetch(`/api/billing/${billId}/pdf${paper === 'receipt' ? '?paper=receipt' : ''}`, { headers: authHeaders() });
    if (!res.ok) throw await failure(res);
    return printBlob(await res.blob(), { title: `Bill ${billId}` });
  });
}

/**
 * Prints a patient document. An uploaded PDF or image prints as the file
 * itself; a generated document (.docx) prints as the server's letterhead page.
 */
export function printPatientDocument(uhid: string, docId: number): Promise<PrintResult | null> {
  return printJob(`doc:${uhid}:${docId}`, async () => {
    const base = `/api/patients/${encodeURIComponent(uhid)}/documents/${docId}`;
    const res = await fetch(`${base}/print`, { headers: authHeaders() });
    if (res.ok) return printHtml(await res.text());
    const err = await failure(res);
    if (res.status !== 409 || err.code !== 'print_original') throw err;
    const file = await fetch(`${base}/file?disposition=inline`, { headers: authHeaders() });
    if (!file.ok) throw await failure(file);
    return printBlob(await file.blob());
  });
}
